'use client'

import { createContext, ReactNode, useContext } from 'react'

import { useUser } from '@/app/contexts/UserContext'
import type { SegmentButtonOverridesQueryResult } from '@/sanity/lib/queries'

type SegmentOverridesContextType = SegmentButtonOverridesQueryResult | null

const SegmentOverridesContext = createContext<SegmentOverridesContextType>(null)

export function SegmentOverridesProvider({
  overrides,
  children,
}: {
  overrides: SegmentButtonOverridesQueryResult
  children: ReactNode
}) {
  return (
    <SegmentOverridesContext.Provider value={overrides}>
      {children}
    </SegmentOverridesContext.Provider>
  )
}

export function useSegmentOverrides() {
  const context = useContext(SegmentOverridesContext)
  if (context === undefined) {
    throw new Error('useSegmentOverrides must be used within a SegmentOverridesProvider')
  }
  return context
}

/**
 * Check if there are any overrides available for a given dataId
 * Used to determine if we should wait for user context before rendering
 */
function hasOverridesForDataId(
  overrides: SegmentButtonOverridesQueryResult | null,
  dataId: string,
): boolean {
  if (!overrides?.overrides) return false
  return overrides.overrides.some((o) => o.elementDataId === dataId)
}

/**
 * Find a button override by dataId that matches the user's segment
 * Returns the first matching override where:
 * 1. elementDataId matches the provided dataId
 * 2. User has at least one segment that matches the override's segmentIds
 *
 * Returns an object with:
 * - override: The matching override or null
 * - shouldWaitForUser: True if button should be hidden until user context loads
 */
export function useButtonOverride(dataId: string | null | undefined) {
  const overrides = useSegmentOverrides()
  const { user, isLoading } = useUser()

  if (!dataId) return { override: null, shouldWaitForUser: false }

  const hasOverrides = hasOverridesForDataId(overrides, dataId)

  // If there are overrides for this button AND user is still loading, hide the button
  if (hasOverrides && isLoading) {
    return { override: null, shouldWaitForUser: true }
  }

  // No overrides for this button, or user is loaded - proceed normally
  if (!hasOverrides) {
    return { override: null, shouldWaitForUser: false }
  }

  const userSegments = user?.segments || []

  // Find override where dataId matches AND user has at least one matching segment
  const matchingOverride =
    overrides?.overrides?.find((o) => {
      if (o.elementDataId !== dataId || !o.segmentIds) return false
      return o.segmentIds.some((segmentId) => userSegments.includes(segmentId))
    }) || null

  return { override: matchingOverride, shouldWaitForUser: false }
}
